// Layout shell that pairs a filters slot (left) with a sort slot (right) and
// coordinates the icon-toggle behavior in compact mode via context.

import React, { useEffect, useMemo, useState } from 'react';
import { useSystemWindowSize } from '../../hooks';
import {
  CollectionToolbarContext,
  type CollectionToolbarContextValue,
  type CollectionToolbarSide,
} from './CollectionToolbarContext';

type CompactBreakpoint = 'sm' | 'md' | 'lg';

const COMPACT_BREAKPOINT_PX: Record<CompactBreakpoint, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
};

type CollectionToolbarProps = {
  filters?: React.ReactNode;
  sort?: React.ReactNode;
  compactBreakpoint?: CompactBreakpoint;
};

const CollectionToolbar: React.FC<CollectionToolbarProps> = ({
  filters,
  sort,
  compactBreakpoint = 'md',
}) => {
  const { width } = useSystemWindowSize();
  const isCompact =
    Boolean(filters) &&
    Boolean(sort) &&
    width > 0 &&
    width < COMPACT_BREAKPOINT_PX[compactBreakpoint];
  const [expanded, setExpanded] = useState<CollectionToolbarSide | null>(null);

  // Auto-collapse when leaving compact mode so the next entry starts clean.
  useEffect(() => {
    if (!isCompact && expanded !== null) {
      setExpanded(null);
    }
  }, [isCompact, expanded]);

  const contextValue = useMemo<CollectionToolbarContextValue>(
    () => ({ isCompact, expanded, setExpanded }),
    [isCompact, expanded]
  );

  return (
    <CollectionToolbarContext.Provider value={contextValue}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        {filters ? <div className="min-w-0">{filters}</div> : <div />}
        {sort ? <div className="min-w-0">{sort}</div> : null}
      </div>
    </CollectionToolbarContext.Provider>
  );
};

export default CollectionToolbar;
